import { RiPlugLine, RiSearchLine } from "@remixicon/react";
import React, {
    ReactElement, ReactNode, useMemo, useState,
} from "react";
import { useGetWorkflowServiceQuery, useGetWorkflowServicesQuery, WorkflowService } from "../../../../__generated__/api";
import Badge from "../../../../components/theme/badge/Badge";
import ButtonTabs from "../../../../components/theme/button/ButtonTabs";
import SettingsContainer from "../../../../components/theme/settings";

import Avatar from "../../../../components/avatar/Avatar";
import Button from "../../../../components/theme/button/Button";
import RouterLink from "../../../../components/theme/button/RouterLink";
import { useDrawer } from "../../../../components/theme/drawer";
import FormGroup from "../../../../components/theme/form-group/FormGroup";
import Text from "../../../../components/theme/text/Text";
import Styles from "./Services.module.scss";
import ViewServiceCard from "./ViewServiceCard";
import Link from "../../../../components/theme/button/Link";
import useRouting, { Paths, useParams } from "../../../../contexts/Routing";
import ViewService from "./ViewServiceV3";
import { useSetTitle } from "../base";
import ViewServiceSettings from "./ViewServiceSettings";
import useServiceInstall from "./ServiceInstall";

export function WorkflowServiceList({
    services,
    onSelected,
}: {
    services: WorkflowService[];
    onSelected: (service: WorkflowService) => void;

}): ReactNode {
    const [filterCategory, setFilterCategory] = useState<string>("all");
    const [search, setSearch] = useState("");

    const list = useMemo(() => {
        let results = services;
        if (search) {
            const searchQuery = search.toLowerCase();
            results = results?.filter(service => service.name.toLowerCase().includes(searchQuery)
                        || service.shortDescription?.toLowerCase().includes(searchQuery));
        }
        if (filterCategory !== "all") {
            results = results.filter(row => row.tags?.includes(filterCategory));
        }

        return results;
    }, [search, filterCategory]);

    const listTags = services?.flatMap(row => row.tags).filterNull().distribution(row => row) ?? {};

    const listButtons = Object.entries(listTags).map(([entry, count]) => ({
        key: entry,
        text: (
            <>
                {entry.split("-").map(item => item.capitalize()).join(" ")}
                {" "}
                <Badge variant="counter" isSelected={filterCategory === entry}>{count}</Badge>
            </>
        ),
    }));
    listButtons.unshift({
        key: "all",
        text: (
            <>
                All
                {" "}
                <Badge variant="counter" isSelected={filterCategory === "all"}>{list?.length ?? 0}</Badge>
            </>
        ),
    });

    const { element: installElement, onInstall } = useServiceInstall();

    return (
        <div>
            {installElement}
            <div className={Styles["list-header"]}>
                <ButtonTabs
                    className={Styles["workflow-card-list-filter"]}
                    onChange={selected => setFilterCategory(selected.key)}
                    values={listButtons}
                    value={filterCategory ?? "all"}
                />
                <FormGroup
                    prefixIcon={<RiSearchLine />}
                    placeholder="Search..."
                    type="search"
                    value={search}
                    size="small"
                    onChange={evt => setSearch(evt.target.value)}
                />
            </div>

            <div className={Styles["workflow-card-list"]}>
                {list?.map(service => (
                    <div className={Styles["workflow-card"]}>
                        <div className={Styles["workflow-card-header"]}>
                            <Avatar
                                isCircle={false}
                                variant="sm-3"
                                src={service.squareIconUrl as string}
                                className={Styles["workflow-card-icon"]}
                            />
                            {service.installedIntegrationIds.isNotEmpty && (
                                <Badge variant="success" prefixIcon={<RiPlugLine />}>
                                    {service.installedIntegrationIds.length}
                                    {" "}
                                    {service.installedIntegrationIds.length > 1 ? "Connections" : "Connection"}
                                </Badge>
                            )}

                        </div>
                        <Text as="strong" textVariant="body-1-semibold" colorVariant="body-primary">
                            {service.name}
                        </Text>
                        <Text
                            as="p"
                            textVariant="body-1-regular"
                            colorVariant="body-secondary"
                            className={Styles["workflow-card-description"]}
                        >
                            {service.shortDescription}
                        </Text>
                        <div className={Styles["workflow-actions"]}>
                            {service.installedIntegrationIds.isNotEmpty ? (
                                <RouterLink
                                    variant="outline-gray"
                                    to={Paths.ADMINISTRATOR_SETTING_ID}
                                    params={{ id: service.id, setting: "workflow-service" }}
                                >
                                    Details
                                </RouterLink>

                            ) : (
                                <>
                                    {service.connection?.url?.url ? (
                                        <Link
                                            target="_blank"
                                            rel="noreferrer"
                                            href={service.connection?.url?.url}
                                            variant="outline-blue"
                                            size="medium"
                                        >
                                            {service.connection?.buttonText || "Install"}
                                        </Link>
                                    ) : (
                                        <Button
                                            variant="outline-blue"
                                            size="medium"
                                            onClick={() => onInstall(service)}
                                        >
                                            {service.connection?.buttonText || "Install"}
                                        </Button>
                                    )}

                                    <Button variant="ghost-gray" size="medium" onClick={() => onSelected(service)}>
                                        Details
                                    </Button>
                                </>
                            )}

                        </div>
                    </div>
                ))}
            </div>

        </div>
    );
}

export function WorkflowServicesDefault(): ReactElement {
    const { data: services, loading: isLoading } = useGetWorkflowServicesQuery();
    const [selectedService, setSelected] = useState<WorkflowService>();

    const { data: service, loading: isServiceLoading } = useGetWorkflowServiceQuery({
        skip: !selectedService,
        variables: {
            id: selectedService?.id,
        },
    });

    useSetTitle("Integrations", []);

    const serviceList = services?.workflowServices;

    const { installedServices, notInstalledServices } = useMemo(() => {
        const installed = serviceList?.filter(current => current.installedIntegrationIds.isNotEmpty);
        const notInstalled = serviceList?.filter(current => !current.installedIntegrationIds.isNotEmpty);

        return {
            installedServices: installed,
            notInstalledServices: notInstalled,
        };
    }, [serviceList]);

    const currentService = service?.workflowService ?? selectedService;

    const { element: drawerElement, setOpen } = useDrawer({
        title: currentService?.name ?? "Loading...",
        onHide: () => setSelected(undefined),
        size: "medium",
        bodyClassName: Styles["service-drawer-container"],
        onSave: () => {
            // asdf
        },
        saveButtonText: currentService?.connection?.buttonText ?? "Install",
        cancelButtonText: "Close",
        body: currentService ? (
            <ViewServiceCard
                isLoading={isServiceLoading}
                service={currentService as WorkflowService}
                className={Styles["service-body"]}
            />
        ) : undefined,
    });

    return (
        <SettingsContainer navigation="tabs">
            {drawerElement}
            <SettingsContainer.Section title="Installed" shouldDisplayTitle={false} className={Styles.services}>
                <SettingsContainer.Setting
                    title="Installed Services"
                    description="The installed set of services"
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isHideTitle
                    isLoading={isLoading}
                    isFullWidth
                >
                    <WorkflowServiceList
                        services={installedServices as WorkflowService[] ?? []}
                        onSelected={value => {
                            setSelected(value);
                            setOpen(true);
                        }}
                    />

                </SettingsContainer.Setting>
            </SettingsContainer.Section>
            <SettingsContainer.Section title="Explore" shouldDisplayTitle={false} className={Styles.services}>
                <SettingsContainer.Setting
                    title="Installed Services"
                    description="The installed set of services"
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isHideTitle
                    isLoading={isLoading}
                >
                    <WorkflowServiceList
                        services={notInstalledServices as WorkflowService[] ?? []}
                        onSelected={value => {
                            setSelected(value);
                            setOpen(true);
                        }}
                    />
                </SettingsContainer.Setting>
            </SettingsContainer.Section>
        </SettingsContainer>
    );
}

export default function WorkflowServices(): ReactNode {
    const { id } = useParams(Paths.ADMINISTRATOR_SETTING_ID);
    const { id: serviceId, childId } = useParams(Paths.ADMINISTRATOR_SETTING_ID_CHILD);
    if (childId) {
        return <ViewServiceSettings id={serviceId ?? id} childId={childId} />;
    } if (id) {
        return <ViewService id={serviceId ?? id} />;
    }
    return <WorkflowServicesDefault />;
}
